import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

const FinOpsTopServices = ({ topServicesCurrentMonth, topServicesPreviousMonth }) => {
  // Sort state - must be declared before any early returns
  const [orderBy, setOrderBy] = useState("cost");
  const [order, setOrder] = useState("desc"); // Default: high to low by cost

  // Use currentMonth data, fallback to empty object
  const currentMonth = topServicesCurrentMonth || {};
  const previousMonth = topServicesPreviousMonth || {};
  
  const hasCurrentMonthData = currentMonth && Object.keys(currentMonth).length > 0;
  const hasPreviousMonthData = previousMonth && Object.keys(previousMonth).length > 0;
  
  // If current month data is not available, use previous month data
  const dataToDisplay = hasCurrentMonthData ? currentMonth : previousMonth;
  const isShowingPreviousMonth = !hasCurrentMonthData && hasPreviousMonthData;

  // Convert object to array and sort
  const servicesArray = useMemo(() => {
    if (!hasCurrentMonthData && !hasPreviousMonthData) {
      return [];
    }

    const array = Object.entries(dataToDisplay)
      .map(([name, value]) => {
        // If showing previous month data, there's no current value to compare
        const currentValue = hasCurrentMonthData ? value : 0;
        const previousValue = previousMonth[name] || 0;
        
        // Only calculate change if we have both current and previous month data
        const change = hasCurrentMonthData && previousValue > 0 
          ? ((currentValue - previousValue) / previousValue) * 100 
          : null;
        
        return {
          name: name.trim(),
          cost: value, // Show the value from the data we're displaying
          previousCost: hasCurrentMonthData ? previousValue : 0,
          changePercent: change,
          isIncrease: change !== null ? change > 0 : null,
        };
      });

    // Sort the array
    const sorted = [...array].sort((a, b) => {
      if (orderBy === "cost") {
        return order === "desc" ? b.cost - a.cost : a.cost - b.cost;
      } else if (orderBy === "name") {
        return order === "desc" 
          ? b.name.localeCompare(a.name) 
          : a.name.localeCompare(b.name);
      }
      return 0;
    });

    return sorted.slice(0, 10); // Top 10
  }, [dataToDisplay, hasCurrentMonthData, previousMonth, orderBy, order]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  
  // If no data at all, show empty state
  if (!hasCurrentMonthData && !hasPreviousMonthData) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            fontWeight: 600,
            color: "text.primary",
            flexShrink: 0,
          }}
        >
          Top Services by Cost
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">No service data available</Typography>
        </Box>
      </Paper>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ mb: 3, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: isShowingPreviousMonth ? 1 : 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Top Services by Cost
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
              fontWeight: 500,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              backgroundColor: "action.hover",
            }}
          >
            MTD
          </Typography>
        </Box>
        {isShowingPreviousMonth && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
              fontStyle: "italic",
            }}
          >
            Current month data unavailable. Displaying previous month data.
          </Typography>
        )}
      </Box>
      <TableContainer
        sx={{
          flex: 1,
          overflowY: "auto",
          maxHeight: "350px",
          // Modern thin scrollbar styling
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#cbd5e1",
            borderRadius: "10px",
            "&:hover": {
              background: "#94a3b8",
            },
          },
          // Firefox scrollbar styling
          scrollbarWidth: "thin",
          scrollbarColor: "#cbd5e1 transparent",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ fontWeight: 600, color: "text.secondary" }}
                sortDirection={orderBy === "name" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={() => handleSort("name")}
                  sx={{
                    "& .MuiTableSortLabel-icon": {
                      opacity: orderBy === "name" ? 1 : 0.3,
                    },
                  }}
                >
                  Service Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 600, color: "text.secondary" }}
                sortDirection={orderBy === "cost" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "cost"}
                  direction={orderBy === "cost" ? order : "desc"}
                  onClick={() => handleSort("cost")}
                  sx={{
                    "& .MuiTableSortLabel-icon": {
                      opacity: orderBy === "cost" ? 1 : 0.3,
                    },
                  }}
                >
                  Cost
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {servicesArray.map((service, index) => (
              <TableRow
                key={service.name}
                sx={{
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        color: "text.primary",
                      }}
                    >
                      {service.name}
                    </Typography>
                    {service.changePercent !== null && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: service.isIncrease ? "error.main" : "success.main",
                        }}
                      >
                        {service.isIncrease ? (
                          <TrendingUpIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 16 }} />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        >
                          {Math.abs(service.changePercent).toFixed(1)}%
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                      }}
                    >
                      {formatCurrency(service.cost)}
                    </Typography>
                    {service.previousCost > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.75rem",
                        }}
                      >
                        Prev: {formatCurrency(service.previousCost)}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default FinOpsTopServices;

